import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Layout, Row, Col, Breadcrumb, Collapse, Descriptions, Tag, Typography,
    Form, Select, InputNumber, Input, Button, Divider, Space, Spin, message, Alert, Progress,
    Tabs, Table, Modal, DatePicker, Tooltip
} from "antd";
import {
    HomeOutlined, ArrowLeftOutlined, PlusOutlined, MinusOutlined,
    ThunderboltOutlined, SearchOutlined, FilterOutlined, ClearOutlined,
    PrinterOutlined, FileExcelOutlined
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../services/axiosInstance";
import { isPermissionAllowed } from "../../../utils/permissionHelper";
import ReportPreviewModal from "../../../components/common/ReportPreviewModal";
import { buildReportPDF } from "../../../utils/reportPdfBuilder";
import { exportRowsToExcel, fmtMoney } from "../../../utils/reportExport";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;
const { RangePicker } = DatePicker;

const HARVESTB_STATUS_COLORS = {
    "Pending": "orange",
    "Ongoing": "blue",
    "Completed": "green",
};

const formatLKR = (value) =>
    `LKR ${parseFloat(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatKg = (value) =>
    Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const HarvestCollectionDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [batch, setBatch] = useState(null);
    const [loading, setLoading] = useState(false);

    // Tabs
    const [activeTab, setActiveTab] = useState("collection");

    // Add Harvest modal state
    const [fields, setFields] = useState([]);
    const [plants, setPlants] = useState([]);
    const [harvestForm] = Form.useForm();
    const [harvestModalOpen, setHarvestModalOpen] = useState(false);
    const [harvestSubmitting, setHarvestSubmitting] = useState(false);

    // Add Wastage modal state
    const [wastageForm] = Form.useForm();
    const [wastageModalOpen, setWastageModalOpen] = useState(false);
    const [wastageSubmitting, setWastageSubmitting] = useState(false);

    // Harvest Collection tab - list state
    const [records, setRecords] = useState([]);
    const [recordsLoading, setRecordsLoading] = useState(false);
    const [recordPagination, setRecordPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    // Wastage Harvest tab - list state
    const [wastage, setWastage] = useState([]);
    const [wastageLoading, setWastageLoading] = useState(false);
    const [wastagePagination, setWastagePagination] = useState({ current: 1, pageSize: 10, total: 0 });

    // Harvest Collection tab - filters
    const [colSearch, setColSearch] = useState("");
    const [colField, setColField] = useState(undefined);
    const [colDates, setColDates] = useState([]);
    const [appliedColSearch, setAppliedColSearch] = useState("");
    const [appliedColField, setAppliedColField] = useState(0);
    const [appliedColDates, setAppliedColDates] = useState([]);

    // Wastage Harvest tab - filters
    const [wasSearch, setWasSearch] = useState("");
    const [wasDates, setWasDates] = useState([]);
    const [appliedWasSearch, setAppliedWasSearch] = useState("");
    const [appliedWasDates, setAppliedWasDates] = useState([]);

    // Print / Excel export state (same PDF layout as Purchase Requests reports)
    const [printVisible, setPrintVisible] = useState(false);
    const [pdfDataUri, setPdfDataUri] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [printTab, setPrintTab] = useState("collection");
    const [printRows, setPrintRows] = useState([]);

    // Guards against stale responses when filters/tab change quickly
    const colReqSeq = useRef(0);
    const wasReqSeq = useRef(0);

    const canHarvest = isPermissionAllowed('harvesting_management_harvest');

    const harvestKg = Form.useWatch("total_kg", harvestForm) || 0;
    const wastageKg = Form.useWatch("reject_kg", wastageForm) || 0;

    const fetchBatch = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/harvesting/ongoing_batch.php?id=${id}`);
            if (res.data.status === "success") setBatch(res.data);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to load batch details.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchBatch();
    }, [fetchBatch]);

    // Load fields for the batch's section
    useEffect(() => {
        if (!batch) return;
        const loadFields = async () => {
            try {
                const res = await api.get(
                    `/masterdropdown/listof_activefields.php?section_id=${batch.section_id}&land_id=${batch.land_id}`
                );
                setFields(res.data.dataset || []);
            } catch {
                setFields([]);
            }
        };
        loadFields();
    }, [batch]);

    // Load plants when field changes (in Add Collection modal)
    const handleFieldChange = async (fieldId) => {
        harvestForm.setFieldsValue({ plant_id: undefined });
        setPlants([]);
        if (!fieldId || !batch) return;
        try {
            const res = await api.get(
                `/masterdropdown/listof_activeplants.php?section_id=${batch.section_id}&land_id=${batch.land_id}`
            );
            setPlants(res.data.dataset || []);
        } catch {
            setPlants([]);
        }
    };

    // Fetch harvest collection records
    const fetchRecords = useCallback(async (page = 1, limit = 10) => {
        if (!batch) return;
        const seq = ++colReqSeq.current;
        setRecordsLoading(true);
        try {
            const res = await api.post("/harvesting/harvest/list_records.php", {
                harvestb_id: batch.harvestb_id,
                search: appliedColSearch,
                field_id: appliedColField,
                start_date: appliedColDates && appliedColDates[0] ? appliedColDates[0].format("YYYY-MM-DD") : "",
                end_date: appliedColDates && appliedColDates[1] ? appliedColDates[1].format("YYYY-MM-DD") : "",
                page,
                limit,
            });
            if (seq !== colReqSeq.current) return; // a newer request superseded this one
            if (res.data.status === "success") {
                setRecords(res.data.dataset || []);
                setRecordPagination({
                    current: page,
                    pageSize: limit,
                    total: res.data.total || 0,
                });
            }
        } catch {
            if (seq === colReqSeq.current) message.error("Failed to load harvest collection records.");
        } finally {
            if (seq === colReqSeq.current) setRecordsLoading(false);
        }
    }, [batch, appliedColSearch, appliedColField, appliedColDates]);

    // Fetch wastage records
    const fetchWastage = useCallback(async (page = 1, limit = 10) => {
        if (!batch) return;
        const seq = ++wasReqSeq.current;
        setWastageLoading(true);
        try {
            const res = await api.post("/harvesting/harvest/list_wastage.php", {
                harvestb_id: batch.harvestb_id,
                search: appliedWasSearch,
                start_date: appliedWasDates && appliedWasDates[0] ? appliedWasDates[0].format("YYYY-MM-DD") : "",
                end_date: appliedWasDates && appliedWasDates[1] ? appliedWasDates[1].format("YYYY-MM-DD") : "",
                page,
                limit,
            });
            if (seq !== wasReqSeq.current) return; // a newer request superseded this one
            if (res.data.status === "success") {
                setWastage(res.data.dataset || []);
                setWastagePagination({
                    current: page,
                    pageSize: limit,
                    total: res.data.total || 0,
                });
            }
        } catch {
            if (seq === wasReqSeq.current) message.error("Failed to load wastage records.");
        } finally {
            if (seq === wasReqSeq.current) setWastageLoading(false);
        }
    }, [batch, appliedWasSearch, appliedWasDates]);

    // Load active tab list when tab changes, filters are applied or batch is refreshed
    useEffect(() => {
        if (!batch) return;
        if (activeTab === "collection") {
            fetchRecords(1, recordPagination.pageSize);
        } else {
            fetchWastage(1, wastagePagination.pageSize);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batch, activeTab, appliedColSearch, appliedColField, appliedColDates, appliedWasSearch, appliedWasDates]);

    // Collection tab filter handlers
    const handleColFilter = () => {
        setAppliedColSearch(colSearch.trim());
        setAppliedColField(colField ? Number(colField) : 0);
        setAppliedColDates(colDates);
    };

    const handleColClear = () => {
        setColSearch("");
        setColField(undefined);
        setColDates([]);
        setAppliedColSearch("");
        setAppliedColField(0);
        setAppliedColDates([]);
    };

    // Wastage tab filter handlers
    const handleWasFilter = () => {
        setAppliedWasSearch(wasSearch.trim());
        setAppliedWasDates(wasDates);
    };

    const handleWasClear = () => {
        setWasSearch("");
        setWasDates([]);
        setAppliedWasSearch("");
        setAppliedWasDates([]);
    };

    // ── Print & Excel export (respects applied filters; no filters = all records) ──

    // Fetch ALL records matching the applied filters (no pagination)
    const fetchAllForExport = async (tab) => {
        const limit = 100000;
        if (tab === "collection") {
            const res = await api.post("/harvesting/harvest/list_records.php", {
                harvestb_id: batch.harvestb_id,
                search: appliedColSearch,
                field_id: appliedColField,
                start_date: appliedColDates && appliedColDates[0] ? appliedColDates[0].format("YYYY-MM-DD") : "",
                end_date: appliedColDates && appliedColDates[1] ? appliedColDates[1].format("YYYY-MM-DD") : "",
                page: 1,
                limit,
            });
            return res.data?.dataset || [];
        }
        const res = await api.post("/harvesting/harvest/list_wastage.php", {
            harvestb_id: batch.harvestb_id,
            search: appliedWasSearch,
            start_date: appliedWasDates && appliedWasDates[0] ? appliedWasDates[0].format("YYYY-MM-DD") : "",
            end_date: appliedWasDates && appliedWasDates[1] ? appliedWasDates[1].format("YYYY-MM-DD") : "",
            page: 1,
            limit,
        });
        return res.data?.dataset || [];
    };

    const buildCollectionFilterLabels = () => {
        const labels = {};
        if (appliedColSearch) labels["Search"] = appliedColSearch;
        if (appliedColField) {
            const f = fields.find((o) => Number(o.value) === Number(appliedColField));
            labels["Field"] = f ? f.label : appliedColField;
        }
        if (appliedColDates && (appliedColDates[0] || appliedColDates[1])) {
            const start = appliedColDates[0] ? appliedColDates[0].format("DD MMM YYYY") : null;
            const end = appliedColDates[1] ? appliedColDates[1].format("DD MMM YYYY") : null;
            labels["Period"] = start && end ? `${start} to ${end}` : start ? `${start} onwards` : `up to ${end}`;
        }
        return labels;
    };

    const buildWastageFilterLabels = () => {
        const labels = {};
        if (appliedWasSearch) labels["Search"] = appliedWasSearch;
        if (appliedWasDates && (appliedWasDates[0] || appliedWasDates[1])) {
            const start = appliedWasDates[0] ? appliedWasDates[0].format("DD MMM YYYY") : null;
            const end = appliedWasDates[1] ? appliedWasDates[1].format("DD MMM YYYY") : null;
            labels["Period"] = start && end ? `${start} to ${end}` : start ? `${start} onwards` : `up to ${end}`;
        }
        return labels;
    };

    // Applied filters (if any) are printed as summary lines in the same style as Batch Ref etc.
    const filterLabelsToSummary = (labels) =>
        Object.entries(labels).map(([label, value]) => ({ label, value }));

    const buildPrintDoc = (tab, rows) => {
        if (tab === "collection") {
            return buildReportPDF({
                title: "HARVEST COLLECTION REPORT",
                subtitle: `Harvest Batch ${batch.harvestb_ref}`,
                columns: [
                    { header: "Collected Date" }, { header: "Field" }, { header: "Plant" },
                    { header: "Quantity", align: "right" }, { header: "Weight (Kg)", align: "right" },
                    { header: "Commercial Cost", align: "right" }, { header: "Expected Income", align: "right" },
                    { header: "Collected By" },
                ],
                rows: rows.map((r) => [
                    r.collect_date ? dayjs(r.collect_date).format("YYYY-MM-DD HH:mm") : "-",
                    r.field_code || "-",
                    r.plant_code || "-",
                    String(r.total_qty ?? 0),
                    fmtMoney(r.total_kg),
                    fmtMoney(r.commercial_cost),
                    fmtMoney(r.expect_income),
                    r.collect_by_name || "-",
                ]),
                summary: [
                    { label: "Batch Ref", value: batch.harvestb_ref },
                    { label: "Land / Section", value: `${batch.land_name || "-"} / ${batch.section_name || "-"}` },
                    { label: "Collected (Kg)", value: `${fmtMoney(batch.total_collect_kg)} / ${fmtMoney(batch.target_kg)} kg` },
                    ...filterLabelsToSummary(buildCollectionFilterLabels()),
                ],
                totalRow: [
                    "TOTAL", "", "",
                    String(rows.reduce((s, r) => s + Number(r.total_qty || 0), 0)),
                    fmtMoney(rows.reduce((s, r) => s + Number(r.total_kg || 0), 0)),
                    fmtMoney(rows.reduce((s, r) => s + Number(r.commercial_cost || 0), 0)),
                    fmtMoney(rows.reduce((s, r) => s + Number(r.expect_income || 0), 0)),
                    "",
                ],
                columnStyles: { 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" } },
            });
        }
        return buildReportPDF({
            title: "WASTAGE HARVEST REPORT",
            subtitle: `Harvest Batch ${batch.harvestb_ref}`,
            columns: [
                { header: "Recorded Date" }, { header: "Quantity", align: "right" },
                { header: "Weight (Kg)", align: "right" }, { header: "Commercial Lost", align: "right" },
                { header: "Reason" }, { header: "Recorded By" },
            ],
            rows: rows.map((r) => [
                r.created_dt ? dayjs(r.created_dt).format("YYYY-MM-DD HH:mm") : "-",
                String(r.reject_qty ?? 0),
                fmtMoney(r.reject_kg),
                fmtMoney(r.commercial_lost),
                r.reject_reason || "-",
                r.created_by_name || "-",
            ]),
            summary: [
                { label: "Batch Ref", value: batch.harvestb_ref },
                { label: "Land / Section", value: `${batch.land_name || "-"} / ${batch.section_name || "-"}` },
                { label: "Wastage (Kg)", value: fmtMoney(batch.quality_reject_kg) },
                ...filterLabelsToSummary(buildWastageFilterLabels()),
            ],
            totalRow: [
                "TOTAL",
                String(rows.reduce((s, r) => s + Number(r.reject_qty || 0), 0)),
                fmtMoney(rows.reduce((s, r) => s + Number(r.reject_kg || 0), 0)),
                fmtMoney(rows.reduce((s, r) => s + Number(r.commercial_lost || 0), 0)),
                "", "",
            ],
            columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
        });
    };

    const handlePrint = async () => {
        if (!batch) return;
        const tab = activeTab;
        setPrinting(true);
        try {
            const rows = await fetchAllForExport(tab);
            if (rows.length === 0) {
                message.warning("No data to print.");
                return;
            }
            setPrintTab(tab);
            setPrintRows(rows);
            setPrintVisible(true);
            setPdfLoading(true);
            setPdfDataUri(null);
            const doc = await buildPrintDoc(tab, rows);
            setPdfDataUri(doc.output("bloburl"));
        } catch (err) {
            console.error("PDF generation failed:", err);
            message.error("Failed to generate PDF preview.");
        } finally {
            setPdfLoading(false);
            setPrinting(false);
        }
    };

    const handleDownloadPDF = async () => {
        try {
            const doc = await buildPrintDoc(printTab, printRows);
            const prefix = printTab === "collection" ? "harvest-collection" : "wastage-harvest";
            doc.save(`${prefix}-${batch.harvestb_ref}-${dayjs().format("YYYYMMDD-HHmm")}.pdf`);
            message.success("PDF downloaded successfully.");
        } catch (err) {
            console.error("PDF download failed:", err);
            message.error("Failed to download PDF.");
        }
    };

    const handleExportExcel = async () => {
        if (!batch) return;
        const tab = activeTab;
        setExporting(true);
        try {
            const rows = await fetchAllForExport(tab);
            if (rows.length === 0) {
                message.warning("No data to export.");
                return;
            }
            if (tab === "collection") {
                exportRowsToExcel({
                    filename: `harvest-collection-${batch.harvestb_ref}`,
                    sheetName: "Harvest Collection",
                    columns: [
                        { title: "Collected Date", dataIndex: "collect_date", width: 18 },
                        { title: "Field", dataIndex: "field_code", width: 14 },
                        { title: "Plant", dataIndex: "plant_code", width: 14 },
                        { title: "Quantity", dataIndex: "total_qty", width: 10 },
                        { title: "Weight (Kg)", dataIndex: "total_kg", width: 12 },
                        { title: "Commercial Cost", dataIndex: "commercial_cost", width: 16 },
                        { title: "Expected Income", dataIndex: "expect_income", width: 16 },
                        { title: "Collected By", dataIndex: "collect_by_name", width: 18 },
                    ],
                    rows: rows.map((r) => ({
                        collect_date: r.collect_date ? dayjs(r.collect_date).format("YYYY-MM-DD HH:mm") : "-",
                        field_code: r.field_code || "-",
                        plant_code: r.plant_code || "-",
                        total_qty: Number(r.total_qty || 0),
                        total_kg: fmtMoney(r.total_kg),
                        commercial_cost: fmtMoney(r.commercial_cost),
                        expect_income: fmtMoney(r.expect_income),
                        collect_by_name: r.collect_by_name || "-",
                    })),
                    extraRows: [[
                        "TOTAL", "", "",
                        rows.reduce((s, r) => s + Number(r.total_qty || 0), 0),
                        fmtMoney(rows.reduce((s, r) => s + Number(r.total_kg || 0), 0)),
                        fmtMoney(rows.reduce((s, r) => s + Number(r.commercial_cost || 0), 0)),
                        fmtMoney(rows.reduce((s, r) => s + Number(r.expect_income || 0), 0)),
                        "",
                    ]],
                });
            } else {
                exportRowsToExcel({
                    filename: `wastage-harvest-${batch.harvestb_ref}`,
                    sheetName: "Wastage Harvest",
                    columns: [
                        { title: "Recorded Date", dataIndex: "created_dt", width: 18 },
                        { title: "Quantity", dataIndex: "reject_qty", width: 10 },
                        { title: "Weight (Kg)", dataIndex: "reject_kg", width: 12 },
                        { title: "Commercial Lost", dataIndex: "commercial_lost", width: 16 },
                        { title: "Reason", dataIndex: "reject_reason", width: 30 },
                        { title: "Recorded By", dataIndex: "created_by_name", width: 18 },
                    ],
                    rows: rows.map((r) => ({
                        created_dt: r.created_dt ? dayjs(r.created_dt).format("YYYY-MM-DD HH:mm") : "-",
                        reject_qty: Number(r.reject_qty || 0),
                        reject_kg: fmtMoney(r.reject_kg),
                        commercial_lost: fmtMoney(r.commercial_lost),
                        reject_reason: r.reject_reason || "-",
                        created_by_name: r.created_by_name || "-",
                    })),
                    extraRows: [[
                        "TOTAL",
                        rows.reduce((s, r) => s + Number(r.reject_qty || 0), 0),
                        fmtMoney(rows.reduce((s, r) => s + Number(r.reject_kg || 0), 0)),
                        fmtMoney(rows.reduce((s, r) => s + Number(r.commercial_lost || 0), 0)),
                        "", "",
                    ]],
                });
            }
            message.success("Excel exported successfully.");
        } catch (err) {
            console.error("Excel export failed:", err);
            message.error("Failed to export Excel.");
        } finally {
            setExporting(false);
        }
    };

    // Submit harvest (Add Collection modal)
    const handleHarvestSubmit = async () => {
        try {
            const values = await harvestForm.validateFields();
            setHarvestSubmitting(true);

            const res = await api.post("/harvesting/harvest/submit.php", {
                harvestb_id: batch.harvestb_id,
                land_id: batch.land_id,
                section_id: batch.section_id,
                field_id: values.field_id,
                plant_id: values.plant_id,
                total_qty: values.total_qty,
                total_kg: values.total_kg,
            });

            if (res.data.status === "success") {
                message.success(res.data.message || "Harvest collection added successfully");
                setHarvestModalOpen(false);
                harvestForm.resetFields();
                setPlants([]);
                fetchBatch();
            } else {
                message.error(res.data.message || "Failed to submit harvest");
            }
        } catch (err) {
            if (err?.errorFields) return; // validation error
            message.error("Failed to submit harvest.");
        } finally {
            setHarvestSubmitting(false);
        }
    };

    // Submit wastage (Add Wastage modal)
    const handleWastageSubmit = async () => {
        try {
            const values = await wastageForm.validateFields();
            setWastageSubmitting(true);

            const res = await api.post("/harvesting/harvest/reject.php", {
                harvestb_id: batch.harvestb_id,
                reject_qty: values.reject_qty,
                reject_kg: values.reject_kg,
                reject_reason: values.reject_reason,
            });

            if (res.data.status === "success") {
                message.success(res.data.message || "Wastage harvest added successfully");
                setWastageModalOpen(false);
                wastageForm.resetFields();
                fetchBatch();
            } else {
                message.error(res.data.message || "Failed to submit wastage");
            }
        } catch (err) {
            if (err?.errorFields) return;
            message.error("Failed to submit wastage.");
        } finally {
            setWastageSubmitting(false);
        }
    };

    const costingRate = batch ? parseFloat(batch.costing_rate || 0) : 0;
    const sellingRate = batch ? parseFloat(batch.selling_rate || 0) : 0;
    const targetKg = batch ? parseFloat(batch.target_kg || 0) : 0;
    const commercialCost = harvestKg * costingRate;
    const expectIncome = harvestKg * sellingRate;
    const commercialLost = wastageKg * costingRate;
    const totalTargetCost = costingRate * targetKg;
    const estimatedRevenue = sellingRate * targetKg;
    const estimatedProfit = estimatedRevenue - totalTargetCost;
    const collectionPercent = batch ? (() => {
        const target = parseFloat(batch.target_kg || 0);
        const collected = parseFloat(batch.total_collect_kg || 0);
        return target > 0 ? Math.min(100, Math.round((collected / target) * 100)) : 0;
    })() : 0;

    const collapseItems = batch ? [
        {
            key: "info",
            label: <Typography.Text strong>Batch Information</Typography.Text>,
            children: (
                <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="Batch Ref" span={2}>
                        <Typography.Text strong>{batch.harvestb_ref}</Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Land">{batch.land_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Section">{batch.section_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Harvest Season">{batch.harvest_season || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Status">
                        <Tag color={HARVESTB_STATUS_COLORS[batch.harvestb_status] || "default"} style={{ fontWeight: 600 }}>
                            {batch.harvestb_status || "-"}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Target Quantity">{Number(batch.target_qty || 0).toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="Target Weight (Kg)">{Number(batch.target_kg || 0).toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="Target Start Date">
                        {batch.harvest_start_date ? dayjs(batch.harvest_start_date).format("YYYY-MM-DD") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Target End Date">
                        {batch.harvest_end_date ? dayjs(batch.harvest_end_date).format("YYYY-MM-DD") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Costing Rate (per Kg)">{formatLKR(batch.costing_rate)}</Descriptions.Item>
                    <Descriptions.Item label="Selling Rate (per Kg)">{formatLKR(batch.selling_rate)}</Descriptions.Item>
                    <Descriptions.Item label="Created By">{batch.creator_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Created Date">
                        {batch.cr_dt ? dayjs(batch.cr_dt).format("YYYY-MM-DD HH:mm") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Updated By">{batch.checker_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Last Updated Date">
                        {batch.ch_dt ? dayjs(batch.ch_dt).format("YYYY-MM-DD HH:mm") : "-"}
                    </Descriptions.Item>
                </Descriptions>
            ),
        },
        {
            key: "summary",
            label: <Typography.Text strong>Collection & Financial Summary</Typography.Text>,
            children: (
                <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="Total Collected Quantity">{Number(batch.total_collect_qty || 0).toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="Total Collected Weight (Kg)">{Number(batch.total_collect_kg || 0).toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="Quality Reject Quantity">{Number(batch.quality_reject_qty || 0).toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="Quality Reject Weight (Kg)">{Number(batch.quality_reject_kg || 0).toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="Total Target Cost">
                        <Typography.Text strong>{formatLKR(totalTargetCost)}</Typography.Text>
                        <Typography.Text type="secondary" style={{ display: "block", fontSize: 11 }}>
                            {costingRate.toFixed(2)} × {Number(targetKg).toLocaleString()} kg
                        </Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Estimated Revenue">
                        <Typography.Text strong>{formatLKR(estimatedRevenue)}</Typography.Text>
                        <Typography.Text type="secondary" style={{ display: "block", fontSize: 11 }}>
                            {sellingRate.toFixed(2)} × {Number(targetKg).toLocaleString()} kg
                        </Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Estimated Profit" span={2}>
                        <Typography.Text
                            strong
                            style={{ fontSize: 16, color: estimatedProfit >= 0 ? "#3f8600" : "#cf1322" }}
                        >
                            {formatLKR(estimatedProfit)}
                        </Typography.Text>
                    </Descriptions.Item>
                </Descriptions>
            ),
        },
    ] : [];

    // Harvest Collection tab table columns
    const recordColumns = [
        {
            title: "Collected Date",
            dataIndex: "collect_date",
            key: "collect_date",
            render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "-"),
        },
        { title: "Field", dataIndex: "field_code", key: "field_code", render: (val) => val || "-" },
        { title: "Plant", dataIndex: "plant_code", key: "plant_code", render: (val) => val || "-" },
        { title: "Quantity", dataIndex: "total_qty", key: "total_qty", align: "right", render: (val) => Number(val || 0).toLocaleString() },
        { title: "Weight (Kg)", dataIndex: "total_kg", key: "total_kg", align: "right", render: (val) => formatKg(val) },
        { title: "Commercial Cost", dataIndex: "commercial_cost", key: "commercial_cost", align: "right", render: (val) => formatLKR(val) },
        { title: "Expected Income", dataIndex: "expect_income", key: "expect_income", align: "right", render: (val) => formatLKR(val) },
        { title: "Collected By", dataIndex: "collect_by_name", key: "collect_by_name", render: (val) => val || "-" },
    ];

    // Wastage Harvest tab table columns
    const wastageColumns = [
        {
            title: "Recorded Date",
            dataIndex: "created_dt",
            key: "created_dt",
            render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "-"),
        },
        { title: "Quantity", dataIndex: "reject_qty", key: "reject_qty", align: "right", render: (val) => Number(val || 0).toLocaleString() },
        { title: "Weight (Kg)", dataIndex: "reject_kg", key: "reject_kg", align: "right", render: (val) => formatKg(val) },
        { title: "Commercial Lost", dataIndex: "commercial_lost", key: "commercial_lost", align: "right", render: (val) => formatLKR(val) },
        { title: "Reason", dataIndex: "reject_reason", key: "reject_reason", ellipsis: true, width: 240, render: (val) => val || "-" },
        { title: "Recorded By", dataIndex: "created_by_name", key: "created_by_name", render: (val) => val || "-" },
    ];

    const tabItems = [
        {
            key: "collection",
            label: "Harvest Collection",
            children: (
                <>
                    {/* Filter bar + Add Collection button */}
                    <div style={{ margin: "0 16px 16px" }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={colSearch}
                                    onChange={(e) => setColSearch(e.target.value)}
                                    onPressEnter={handleColFilter}
                                    placeholder="Field, plant, collected by"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Field:</div>
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    value={colField}
                                    onChange={setColField}
                                    style={{ width: "100%" }}
                                    placeholder="Choose Field"
                                    allowClear
                                    options={fields}
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Date Range:</div>
                                <RangePicker
                                    value={colDates}
                                    onChange={setColDates}
                                    style={{ width: "100%" }}
                                />
                            </Col>
                            <Col xs={24} sm={24} lg={10}>
                                <div className="fluid-filter-compact-actions" style={{ flexWrap: "wrap", height: "auto", minHeight: 32, rowGap: 8 }}>
                                    <Button
                                        type="primary"
                                        icon={<FilterOutlined />}
                                        onClick={handleColFilter}
                                        className="fluid-btn-primary"
                                    >
                                        Filter
                                    </Button>
                                    <Button
                                        icon={<ClearOutlined />}
                                        onClick={handleColClear}
                                        className="fluid-btn-secondary"
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => setHarvestModalOpen(true)}
                                        className="fluid-btn-primary"
                                    >
                                        Add Collection
                                    </Button>
                                    <Tooltip title="Print (respects current filters)">
                                        <Button
                                            icon={<PrinterOutlined />}
                                            onClick={handlePrint}
                                            loading={printing}
                                        >
                                            Print
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Export to Excel (respects current filters)">
                                        <Button
                                            icon={<FileExcelOutlined style={{ color: "#52c41a" }} />}
                                            onClick={handleExportExcel}
                                            loading={exporting}
                                        >
                                            Excel
                                        </Button>
                                    </Tooltip>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {/* Harvest collection records table */}
                    <div className="fluid-table" style={{ padding: "0 16px 16px" }}>
                        <Table
                            columns={recordColumns}
                            dataSource={records}
                            rowKey="harvestcol_id"
                            loading={recordsLoading}
                            bordered
                            size="small"
                            scroll={{ x: "max-content" }}
                            pagination={{
                                current: recordPagination.current,
                                pageSize: recordPagination.pageSize,
                                total: recordPagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={(pag) => fetchRecords(pag.current, pag.pageSize)}
                        />
                    </div>
                </>
            ),
        },
        {
            key: "wastage",
            label: "Wastage Harvest",
            children: (
                <>
                    {/* Filter bar + Add Wastage button */}
                    <div style={{ margin: "0 16px 16px" }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} lg={6}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={wasSearch}
                                    onChange={(e) => setWasSearch(e.target.value)}
                                    onPressEnter={handleWasFilter}
                                    placeholder="Reason, recorded by"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Date Range:</div>
                                <RangePicker
                                    value={wasDates}
                                    onChange={setWasDates}
                                    style={{ width: "100%" }}
                                />
                            </Col>
                            <Col xs={24} sm={24} lg={12}>
                                <div className="fluid-filter-compact-actions" style={{ flexWrap: "wrap", height: "auto", minHeight: 32, rowGap: 8 }}>
                                    <Button
                                        type="primary"
                                        icon={<FilterOutlined />}
                                        onClick={handleWasFilter}
                                        className="fluid-btn-primary"
                                    >
                                        Filter
                                    </Button>
                                    <Button
                                        icon={<ClearOutlined />}
                                        onClick={handleWasClear}
                                        className="fluid-btn-secondary"
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => setWastageModalOpen(true)}
                                        className="fluid-btn-primary"
                                    >
                                        Add Wastage
                                    </Button>
                                    <Tooltip title="Print (respects current filters)">
                                        <Button
                                            icon={<PrinterOutlined />}
                                            onClick={handlePrint}
                                            loading={printing}
                                        >
                                            Print
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Export to Excel (respects current filters)">
                                        <Button
                                            icon={<FileExcelOutlined style={{ color: "#52c41a" }} />}
                                            onClick={handleExportExcel}
                                            loading={exporting}
                                        >
                                            Excel
                                        </Button>
                                    </Tooltip>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {/* Wastage records table */}
                    <div className="fluid-table" style={{ padding: "0 16px 16px" }}>
                        <Table
                            columns={wastageColumns}
                            dataSource={wastage}
                            rowKey="harvestwas_id"
                            loading={wastageLoading}
                            bordered
                            size="small"
                            scroll={{ x: "max-content" }}
                            pagination={{
                                current: wastagePagination.current,
                                pageSize: wastagePagination.pageSize,
                                total: wastagePagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={(pag) => fetchWastage(pag.current, pag.pageSize)}
                        />
                    </div>
                </>
            ),
        },
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Harvesting' },
                                { title: 'Harvest Collection' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <ThunderboltOutlined /> Harvest Collection
                        </h1>
                        <p className="fluid-page-subtitle">
                            {batch ? `Harvest batch ${batch.harvestb_ref} - record harvest collections and wastages` : "Harvest batch details"}
                        </p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate('/harvesting/harvest-collection')}
                            className="fluid-btn-secondary"
                        >
                            Back to Batches
                        </Button>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <Spin spinning={loading}>
                    {batch && (
                        <>
                            {/* Collapsible batch details */}
                            <div className="fluid-card" style={{ marginBottom: 16 }}>
                                <Collapse
                                    items={collapseItems}
                                    defaultActiveKey={["info", "summary"]}
                                    expandIconPosition="end"
                                />
                            </div>

                            {!canHarvest ? (
                                <div className="fluid-card">
                                    <Alert
                                        type="warning"
                                        showIcon
                                        message="No permission to record harvests"
                                        description="You can view the batch details above, but you do not have the 'harvesting_management_harvest' permission to submit harvests or wastages."
                                    />
                                </div>
                            ) : (
                                <div className="fluid-card">
                                    <div style={{ padding: "16px 16px 0" }}>
                                        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Spin>
            </Content>

            <Footerdiv />

            {/* Add Collection Modal */}
            <Modal
                title={<Space><PlusOutlined style={{ color: "#1677ff" }} /> Add Harvest Collection</Space>}
                open={harvestModalOpen}
                onCancel={() => { setHarvestModalOpen(false); harvestForm.resetFields(); setPlants([]); }}
                footer={null}
                width={620}
                maskClosable={false}
                forceRender
            >
                <Form form={harvestForm} layout="vertical" onFinish={handleHarvestSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="field_id" label="Field">
                                <Select allowClear
                                    placeholder="Select Field"
                                    showSearch
                                    optionFilterProp="label"
                                    onChange={handleFieldChange}
                                    options={fields}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="plant_id" label="Plant">
                                <Select allowClear
                                    placeholder="Select Plant"
                                    showSearch
                                    optionFilterProp="label"
                                    options={plants}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="total_qty"
                                label="Total Quantity"
                                rules={[{ required: true, message: "Please enter quantity" }]}
                            >
                                <InputNumber min={1} style={{ width: "100%" }} placeholder="Enter quantity" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="total_kg"
                                label="Total Kg"
                                rules={[{ required: true, message: "Please enter weight" }]}
                            >
                                <InputNumber
                                    min={0.01}
                                    step={0.01}
                                    precision={2}
                                    style={{ width: "100%" }}
                                    placeholder="Enter weight in kg"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div
                        style={{
                            background: "#f6f8fa",
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            padding: "8px 12px",
                            marginBottom: 16,
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                Collection Progress
                            </Typography.Text>
                            <Typography.Text style={{ fontSize: 12 }}>
                                {Number(batch?.total_collect_kg || 0).toLocaleString()} /{" "}
                                {Number(batch?.target_kg || 0).toLocaleString()} kg
                            </Typography.Text>
                        </div>
                        <Progress percent={collectionPercent} size="small" strokeColor="#1677ff" />
                    </div>

                    <Divider style={{ margin: "8px 0 16px" }} />

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Commercial Cost" style={{ marginBottom: 12 }}>
                                <Typography.Text strong style={{ fontSize: 15, color: "#cf1322" }}>
                                    {formatLKR(commercialCost)}
                                </Typography.Text>
                                <Typography.Text type="secondary" style={{ display: "block", fontSize: 11 }}>
                                    {harvestKg} kg × {costingRate.toFixed(2)}
                                </Typography.Text>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Expected Income" style={{ marginBottom: 12 }}>
                                <Typography.Text strong style={{ fontSize: 15, color: "#3f8600" }}>
                                    {formatLKR(expectIncome)}
                                </Typography.Text>
                                <Typography.Text type="secondary" style={{ display: "block", fontSize: 11 }}>
                                    {harvestKg} kg × {sellingRate.toFixed(2)}
                                </Typography.Text>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item style={{ marginBottom: 0 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<PlusOutlined />}
                            loading={harvestSubmitting}
                            style={{ width: "50%" }}
                        >
                            Submit Collection
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Add Wastage Modal */}
            <Modal
                title={<Space><MinusOutlined style={{ color: "#cf1322" }} /> Add Wastage Harvest</Space>}
                open={wastageModalOpen}
                onCancel={() => { setWastageModalOpen(false); wastageForm.resetFields(); }}
                footer={null}
                width={560}
                maskClosable={false}
                forceRender
            >
                <Form form={wastageForm} layout="vertical" onFinish={handleWastageSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="reject_qty"
                                label="Wastage Quantity"
                                rules={[{ required: true, message: "Please enter quantity" }]}
                            >
                                <InputNumber min={1} style={{ width: "100%" }} placeholder="Enter quantity" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="reject_kg"
                                label="Wastage Kg"
                                rules={[{ required: true, message: "Please enter weight" }]}
                            >
                                <InputNumber
                                    min={0.01}
                                    step={0.01}
                                    precision={2}
                                    style={{ width: "100%" }}
                                    placeholder="Enter weight in kg"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="reject_reason"
                        label="Wastage Reason"
                        rules={[{ required: true, message: "Please enter a reason" }]}
                    >
                        <Input.TextArea
                            rows={3}
                            placeholder="Enter wastage reason"
                            maxLength={200}
                            showCount
                        />
                    </Form.Item>

                    <Divider style={{ margin: "8px 0 16px" }} />

                    <Form.Item label="Commercial Lost" style={{ marginBottom: 12 }}>
                        <Typography.Text strong style={{ fontSize: 15, color: "#cf1322" }}>
                            {formatLKR(commercialLost)}
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ display: "block", fontSize: 11 }}>
                            {wastageKg} kg × {costingRate.toFixed(2)}
                        </Typography.Text>
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0 }}>
                        <Button
                            danger
                            type="primary"
                            htmlType="submit"
                            icon={<MinusOutlined />}
                            loading={wastageSubmitting}
                            style={{ width: "50%" }}
                        >
                            Submit Wastage
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Print Preview Modal (same PDF layout as Purchase Requests reports) */}
            <ReportPreviewModal
                visible={printVisible}
                onCancel={() => setPrintVisible(false)}
                title={printTab === "collection" ? "Harvest Collection Report" : "Wastage Harvest Report"}
                pdfDataUri={pdfDataUri}
                loading={pdfLoading}
                showFiltersOption={false}
                onDownload={handleDownloadPDF}
            />
        </Layout>
    );
};

export default HarvestCollectionDetail;
